import isEmpty from 'lodash/fp/isEmpty';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import BlockLayout from 'Blocks/BlockLayout';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Divider = styled.span<{ bgcolor?: string; height?: number }>`
  display: block;
  width: 100%;
  height: ${({ height }) => height || 1}px;
  margin: 20px 0px;

  background-color: ${({ bgcolor }) => bgcolor || theme.colors.tertiary['100']};
`;

const NumbersWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
`;

// @Benoit: this only works when variant is `3up`
const NumbersWrapperWithBorder = styled(NumbersWrapper)`
  div:nth-of-type(n) {
    border-top: 0.5px solid ${theme.colors.primary[400]};
  }

  ${legacyTheme.breakpoints.up('lg')} {
    div:nth-of-type(n) {
      border-top: none;
      border-right: 0.5px solid ${theme.colors.primary[400]};
      border-bottom: 0.5px solid ${theme.colors.primary[400]};
    }
    div:nth-of-type(3n) {
      border-right: none;
    }
    div:nth-last-of-type(-n + 3) {
      border-bottom: none;
    }
  }
`;

const CardNumber = styled.div<{ variant: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 28px 0px;

  box-sizing: content-box;

  ${legacyTheme.breakpoints.up('md')} {
    flex-direction: column;
    justify-content: flex-start;
    max-width: ${({ variant }) => (variant === '3up' ? 360 : 264)}px;
    padding: 40px 18px;
  }

  & * {
    box-sizing: border-box;
  }
`;

const StyledNumber = styled(Typography)`
  flex: 1;
  max-width: 327px;
  margin: 0 auto;
  padding-right: 8px;

  ${legacyTheme.breakpoints.up('sm')} {
    padding-right: 0px;
  }
`;

const StyledTypography = styled(Typography)`
  width: 100%;
`;

const DislaimersContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  ${legacyTheme.breakpoints.up('md')} {
    flex-direction: row;
  }
`;

const DisclaimerWrapper = styled.div<{ hasBorder: boolean }>`
  flex: 1;
  padding: 28px 0px;
  border-bottom: ${({ hasBorder }) =>
    hasBorder ? `0.2px solid ${theme.colors.primary[400]}` : 'none'};

  ${legacyTheme.breakpoints.up('md')} {
    padding: 0px 48px;
    border-right: ${({ hasBorder }) =>
      hasBorder ? `0.2px solid ${theme.colors.primary[400]}` : 'none'};
    border-bottom: none;
  }
`;

const Label = styled(Typography)`
  flex: 4;
`;

type NumbersProps = {
  backgroundColor?: string;
  className?: string;
  dataTestId?: string;
  description?: string;
  DescriptionProps: {
    color?: string;
    marginBottom?: number;
    variant?: string;
  };
  disclaimer?: React.ReactNode;
  DisclaimerProps: {
    color: string;
  };
  disclaimers?: string[];
  hasBorder?: boolean;
  hasDivider?: boolean;
  LabelProps: {
    color: string;
    variant: string;
  };
  numbers: {
    label: React.ReactNode;
    number: string;
  }[];
  NumberProps: {
    bold?: boolean;
    color?: string;
    variant?: string;
  };
  title: string;
  TitleProps: {
    bold?: boolean;
    color?: string;
    marginBottom?: number;
    variantDesktop?: string;
    variantMobile?: string;
  };
  variant?: string;
};

export const Numbers = ({
  backgroundColor = theme.colors.neutral[200],
  className = '',
  dataTestId = '',
  description = '',
  DescriptionProps = {
    color: 'grey',
    marginBottom: 0,
    variant: 'p1',
  },
  disclaimer = '',
  DisclaimerProps = {
    color: 'grey',
  },
  disclaimers = [],
  hasBorder = false,
  hasDivider = false,
  LabelProps = {
    color: 'grey',
    variant: 'p1',
  },
  NumberProps = {
    bold: false,
    color: 'noir',
    variant: 'hero2',
  },
  numbers,
  title,
  variant = '4up',
  TitleProps = {
    bold: false,
    color: 'noir',
    marginBottom: 0,
    variantDesktop: 'h3',
    variantMobile: 'h3',
  },
}: NumbersProps) => {
  const { isMobile } = useResponsiveVariant();
  const Wrapper = hasBorder ? NumbersWrapperWithBorder : NumbersWrapper;

  return (
    // @ts-expect-error - BlockLayout has no TS types for now
    <BlockLayout
      backgroundColor={backgroundColor}
      contentAlign="center"
      contentWidth="veryWide"
      dataTestId={dataTestId}
    >
      <StyledTypography
        align={hasBorder && isMobile ? 'left' : 'center'}
        bold={TitleProps.bold}
        // @ts-expect-error - TitleProps.color should be fined grained for Typography
        color={TitleProps.color}
        markupName="h2"
        paragraph
        // @ts-expect-error - TitleProps.variant should be fined grained for Typography
        variant={isMobile ? TitleProps.variantMobile : TitleProps.variantDesktop}
      >
        {title}
      </StyledTypography>
      {TitleProps.marginBottom && <Spacer size={TitleProps.marginBottom} />}
      {description && (
        <StyledTypography
          align={hasBorder && isMobile ? 'left' : 'center'}
          // @ts-expect-error - TitleProps.color should be fined grained for Typography
          color={DescriptionProps.color}
          paragraph
          style={{ marginBottom: DescriptionProps.marginBottom }}
          // @ts-expect-error - TitleProps.variant should be fined grained for Typography
          variant={DescriptionProps.variant}
        >
          {description}
        </StyledTypography>
      )}
      {hasDivider && <Divider />}
      <Wrapper className={className}>
        {numbers.map(({ number, label }) => {
          return (
            <CardNumber key={`${number}-${label}`} variant={variant}>
              {number && (
                <StyledNumber
                  bold={NumberProps.bold}
                  // @ts-expect-error - TitleProps.color should be fined grained for Typography
                  color={NumberProps.color}
                  markupName="p"
                  // @ts-expect-error - TitleProps.variant should be fined grained for Typography
                  variant={NumberProps.variant}
                >
                  {number}
                </StyledNumber>
              )}
              {label && (
                <Label
                  align={isMobile && hasBorder ? 'left' : 'center'}
                  // @ts-expect-error - TitleProps.color should be fined grained for Typography
                  color={LabelProps.color}
                  // @ts-expect-error - TitleProps.variant should be fined grained for Typography
                  variant={LabelProps.variant}
                >
                  {label}
                </Label>
              )}
            </CardNumber>
          );
        })}
      </Wrapper>
      {disclaimer && (
        <>
          {!isMobile && <Spacer size={60} />}
          <Divider bgcolor={theme.colors.primary[400]} height={0.2} />
          <Spacer size={24} />

          <StyledTypography
            align={hasBorder && isMobile ? 'left' : 'center'}
            // @ts-expect-error - TitleProps.color should be fined grained for Typography
            color={DisclaimerProps.color}
            italic
            variant="p2"
          >
            {disclaimer}
          </StyledTypography>
        </>
      )}
      {!isEmpty(disclaimers) && (
        <>
          {!isMobile && <Spacer size={60} />}
          <Divider bgcolor={theme.colors.primary[400]} height={0.2} />
          {!isMobile && <Spacer size={28} />}
          <DislaimersContainer>
            {disclaimers.map((disc, index) => (
              <DisclaimerWrapper key={disc} hasBorder={index !== disclaimers.length - 1}>
                <Typography
                  align={hasBorder && isMobile ? 'left' : 'center'}
                  // @ts-expect-error - TitleProps.color should be fined grained for Typography
                  color={DisclaimerProps.color}
                  italic
                  variant="p2"
                >
                  {disc}
                </Typography>
              </DisclaimerWrapper>
            ))}
          </DislaimersContainer>
        </>
      )}
    </BlockLayout>
  );
};
